import type {
  AnalyticsInterval,
  AnalyticsMetricName,
  AnalyticsRange,
  AnalyticsSplitName,
  StaffAnalyticsMetric,
  StaffAnalyticsSelection,
  StaffAnalyticsVisits,
} from "@lafayette/schemas";
import { type InfiniteData, useInfiniteQuery, useQuery } from "@tanstack/react-query";

import type { CustomerView } from "../queries";
import { useAnalyticsRuntime } from "../runtime";
import type { Zoom } from "./zoom-math";

// The reads of the Explore tab. Like every read of the area they run inside the audited opening of the view (the `explore` tab), keep nothing in
// the cache after the page lets go and are not retried by the library. They are enabled only on the Explore tab, once its opening has answered:
// an opening of another tab is not valid for these reads, so a read there would be refused and would write a false denial row.

const READ_OPTIONS = {
  gcTime: 0,
  retry: false,
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
} as const;

export type ExploreWindow = Readonly<{
  customerId: string;
  range: AnalyticsRange;
  zoom: Zoom | null;
  enabled: boolean;
}>;

const windowQuery = (range: AnalyticsRange, zoom: Zoom | null, tz: string) => ({
  range,
  tz,
  ...(zoom === null ? {} : { from: zoom.from, to: zoom.to }),
});

const keyOf = (zoom: Zoom | null) => zoom?.from.concat("/", zoom.to) ?? "";

/**
 * Keeps the previous answer while a new window loads, but ONLY for the same customer (the customer id is at `position` of the query key). When
 * the staff member switches to another customer, the numbers of the first one are never shown under the second.
 */
function keepForCustomer<T>(customerId: string, position: number) {
  return (
    previous: T | undefined,
    previousQuery: Readonly<{ queryKey: readonly unknown[] }> | undefined,
  ): T | undefined => (previousQuery?.queryKey[position] === customerId ? previous : undefined);
}

export type ExploreMetricParams = ExploreWindow &
  Readonly<{
    metric: AnalyticsMetricName;
    interval: AnalyticsInterval | null;
    split: AnalyticsSplitName;
    compare: boolean;
  }>;

export function useExploreMetric(view: CustomerView, params: ExploreMetricParams, label = "main") {
  const { explore, timeZone } = useAnalyticsRuntime();
  const tz = timeZone();
  return useQuery<StaffAnalyticsMetric>({
    queryKey: [
      "analytics-explore-metric",
      label,
      params.customerId,
      params.metric,
      params.range,
      keyOf(params.zoom),
      tz,
      params.interval,
      params.split,
      params.compare,
    ],
    queryFn: ({ signal }) =>
      view.read((openingId) =>
        explore.metric(
          params.customerId,
          openingId,
          params.metric,
          {
            ...windowQuery(params.range, params.zoom, tz),
            ...(params.interval === null ? {} : { interval: params.interval }),
            split: params.split,
            compare: params.compare ? "previous_period" : "none",
          },
          { signal },
        ),
      ),
    enabled: params.enabled && view.ready,
    placeholderData: keepForCustomer<StaffAnalyticsMetric>(params.customerId, 2),
    ...READ_OPTIONS,
  });
}

export function useExploreSelection(
  view: CustomerView,
  params: ExploreWindow & { compare: boolean },
) {
  const { explore, timeZone } = useAnalyticsRuntime();
  const tz = timeZone();
  return useQuery<StaffAnalyticsSelection>({
    queryKey: [
      "analytics-explore-selection",
      params.customerId,
      params.range,
      keyOf(params.zoom),
      tz,
      params.compare,
    ],
    queryFn: ({ signal }) =>
      view.read((openingId) =>
        explore.selection(
          params.customerId,
          openingId,
          {
            ...windowQuery(params.range, params.zoom, tz),
            compare: params.compare ? "previous_period" : "none",
          },
          { signal },
        ),
      ),
    enabled: params.enabled && view.ready,
    placeholderData: keepForCustomer<StaffAnalyticsSelection>(params.customerId, 1),
    ...READ_OPTIONS,
  });
}

const VISITS_PAGE = 20;

export function useExploreVisits(view: CustomerView, params: ExploreWindow) {
  const { explore, timeZone } = useAnalyticsRuntime();
  const tz = timeZone();
  return useInfiniteQuery<StaffAnalyticsVisits>({
    queryKey: ["analytics-explore-visits", params.customerId, params.range, keyOf(params.zoom), tz],
    queryFn: ({ pageParam, signal }) =>
      view.read((openingId) =>
        explore.visits(
          params.customerId,
          openingId,
          {
            ...windowQuery(params.range, params.zoom, tz),
            limit: VISITS_PAGE,
            ...(typeof pageParam === "string" ? { cursor: pageParam } : {}),
          },
          { signal },
        ),
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    enabled: params.enabled && view.ready,
    placeholderData: keepForCustomer<InfiniteData<StaffAnalyticsVisits, unknown>>(
      params.customerId,
      1,
    ),
    ...READ_OPTIONS,
  });
}
